import { t } from "../i18n"
import { Controller } from "@hotwired/stimulus"

// Interactive ROI calculator — implements the 4-step method documented in
// docs/marketing/PRICING_AND_ROI.md ("How to Calculate Your ROI"):
//   Step 1 — current annual cost (lost/misplaced equipment, emergency vs
//            preventive maintenance, manual tracking time, compliance).
//   Step 2 — plan cost, following the live pricing-page operator curve
//            (see app/views/pages/pricing.html.erb) rather than any retired
//            starter/kickstart price.
//   Step 3 — savings, applying the doc's conservative reduction percentages.
//   Step 4 — net savings, ROI %, and payback period.
//
// Everything here is pure client-side arithmetic — no backend calculation
// model, no network request for the live numbers. The only server round
// trip is the optional, gated "email me this report" form, which reuses the
// existing DemoRequest model.
export default class extends Controller {
  static targets = [
    "equipmentValue", "lostPercent",
    "avgRepairCost", "repairsPerYear", "preventableRate",
    "hoursPerWeek", "hourlyRate",
    "complianceFines", "auditHours", "auditHourlyRate",
    "freeTier", "teamSizeSlider", "teamSizeLabel", "monthlyCost",
    "equipmentRecoveryRate", "emergencyReductionRate", "adminReductionRate", "complianceReductionRate",
    "industryPreset", "presetNote",
    "outLostEquipment", "outEmergencyTotal", "outEmergencyWaste", "outAdminTime", "outCompliance", "outCurrentTotal",
    "outPlanAnnual",
    "outSavingsEquipment", "outSavingsEmergency", "outSavingsAdmin", "outSavingsCompliance", "outSavingsTotal",
    "outNetSavings", "outRoiPercent", "outPaybackDays", "outPaybackMonths",
    "emailGatePanel", "reportMessage"
  ]

  // Same 9 stops published on the pricing page's "Interactive Volume
  // Calculator" (app/views/pages/pricing.html.erb) — kept in sync manually
  // since that page has no JSON endpoint to fetch from.
  static OPERATOR_TIERS = [
    { users: 25, monthly: 850 },
    { users: 50, monthly: 1450 },
    { users: 100, monthly: 2450 },
    { users: 250, monthly: 4800 },
    { users: 1000, monthly: 14500 },
    { users: 5000, monthly: 42000 },
    { users: 10000, monthly: 65000 },
    { users: 25000, monthly: 115000 },
    { users: 50000, monthly: 175000 }
  ]

  static INDUSTRY_PRESETS = {
    manufacturing: {
      labelKey: "presets.manufacturing.label",
      equipmentValue: 2000000, lostPercent: 2.25,
      avgRepairCost: 21000, repairsPerYear: 12, preventableRate: 100,
      hoursPerWeek: 11, hourlyRate: 40,
      complianceFines: 18000, auditHours: 0, auditHourlyRate: 0,
      monthlyCost: 2450, teamSizeIndex: 2,
      equipmentRecoveryRate: 90, emergencyReductionRate: 57, adminReductionRate: 80, complianceReductionRate: 90,
      noteKey: "presets.manufacturing.note"
    },
    construction: {
      labelKey: "presets.construction.label",
      equipmentValue: 620000, lostPercent: 10,
      avgRepairCost: 53000, repairsPerYear: 1, preventableRate: 100,
      hoursPerWeek: 0, hourlyRate: 0,
      complianceFines: 12000, auditHours: 160, auditHourlyRate: 50,
      monthlyCost: 1450, teamSizeIndex: 1,
      equipmentRecoveryRate: 85, emergencyReductionRate: 76, adminReductionRate: 80, complianceReductionRate: 84,
      noteKey: "presets.construction.note"
    }
  }

  connect() {
    this.calculate()
  }

  // --- Inputs -------------------------------------------------------------

  num(target, fallback = 0) {
    if (!target) return fallback
    const value = parseFloat(target.value)
    return Number.isFinite(value) && value >= 0 ? value : fallback
  }

  // --- Actions --------------------------------------------------------------

  calculate() {
    const results = this.compute()
    this.render(results)
    if (this.hasReportMessageTarget) {
      this.reportMessageTarget.value = this.buildReportText(results)
    }
  }

  onTeamSizeSlider() {
    const idx = parseInt(this.teamSizeSliderTarget.value, 10)
    const tier = this.constructor.OPERATOR_TIERS[idx]
    if (!tier) return

    if (this.hasTeamSizeLabelTarget) {
      this.teamSizeLabelTarget.textContent = t("javascript.controllers.roi_calculator_controller.people_count", { count: tier.users, formatted: tier.users.toLocaleString() })
    }
    this.monthlyCostTarget.value = tier.monthly
    this.calculate()
  }

  applyPreset() {
    const key = this.industryPresetTarget.value
    const preset = this.constructor.INDUSTRY_PRESETS[key]

    if (this.hasPresetNoteTarget) {
      this.presetNoteTarget.textContent = preset ? t(`javascript.controllers.roi_calculator_controller.${preset.noteKey}`) : ""
      this.presetNoteTarget.classList.toggle("hidden", !preset)
    }
    if (!preset) return

    this.equipmentValueTarget.value = preset.equipmentValue
    this.lostPercentTarget.value = preset.lostPercent
    this.avgRepairCostTarget.value = preset.avgRepairCost
    this.repairsPerYearTarget.value = preset.repairsPerYear
    this.preventableRateTarget.value = preset.preventableRate
    this.hoursPerWeekTarget.value = preset.hoursPerWeek
    this.hourlyRateTarget.value = preset.hourlyRate
    this.complianceFinesTarget.value = preset.complianceFines
    this.auditHoursTarget.value = preset.auditHours
    this.auditHourlyRateTarget.value = preset.auditHourlyRate
    this.equipmentRecoveryRateTarget.value = preset.equipmentRecoveryRate
    this.emergencyReductionRateTarget.value = preset.emergencyReductionRate
    this.adminReductionRateTarget.value = preset.adminReductionRate
    this.complianceReductionRateTarget.value = preset.complianceReductionRate

    if (this.hasFreeTierTarget) this.freeTierTarget.checked = false
    this.monthlyCostTarget.value = preset.monthlyCost
    if (this.hasTeamSizeSliderTarget) {
      this.teamSizeSliderTarget.value = preset.teamSizeIndex
      const tier = this.constructor.OPERATOR_TIERS[preset.teamSizeIndex]
      if (tier && this.hasTeamSizeLabelTarget) {
        this.teamSizeLabelTarget.textContent = t("javascript.controllers.roi_calculator_controller.people_count", { count: tier.users, formatted: tier.users.toLocaleString() })
      }
    }

    this.calculate()
  }

  toggleEmailGate() {
    this.emailGatePanelTarget.classList.toggle("hidden")
    if (!this.emailGatePanelTarget.classList.contains("hidden")) {
      const nameField = this.emailGatePanelTarget.querySelector("input[name='demo_request[name]']")
      if (nameField) nameField.focus()
    }
  }

  // --- Core arithmetic (Steps 1-4) ------------------------------------------

  compute() {
    // Step 1 — current annual cost.
    const equipmentValue = this.num(this.equipmentValueTarget)
    const lostPercent = this.num(this.lostPercentTarget)
    const lostEquipmentCost = equipmentValue * (lostPercent / 100)

    const avgRepairCost = this.num(this.avgRepairCostTarget)
    const repairsPerYear = this.num(this.repairsPerYearTarget)
    const preventableRate = this.num(this.preventableRateTarget)
    const emergencyTotal = avgRepairCost * repairsPerYear
    const emergencyWaste = emergencyTotal * (preventableRate / 100)

    const hoursPerWeek = this.num(this.hoursPerWeekTarget)
    const hourlyRate = this.num(this.hourlyRateTarget)
    const weeksPerYear = 50
    const adminTimeCost = hoursPerWeek * hourlyRate * weeksPerYear

    const complianceFines = this.num(this.complianceFinesTarget)
    const auditHours = this.num(this.auditHoursTarget)
    const auditHourlyRate = this.num(this.auditHourlyRateTarget)
    const complianceCost = complianceFines + (auditHours * auditHourlyRate)

    const currentTotal = lostEquipmentCost + emergencyWaste + adminTimeCost + complianceCost

    // Step 2 — plan cost, from the live pricing-page operator curve.
    const isFree = this.hasFreeTierTarget && this.freeTierTarget.checked
    const monthlyCost = isFree ? 0 : this.num(this.monthlyCostTarget)
    const planAnnualCost = monthlyCost * 12

    // Step 3 — savings, at the doc's conservative reduction percentages.
    const equipmentRecoveryRate = this.num(this.equipmentRecoveryRateTarget, 90)
    const emergencyReductionRate = this.num(this.emergencyReductionRateTarget, 40)
    const adminReductionRate = this.num(this.adminReductionRateTarget, 80)
    const complianceReductionRate = this.num(this.complianceReductionRateTarget, 90)

    const savingsEquipment = lostEquipmentCost * (equipmentRecoveryRate / 100)
    const savingsEmergency = emergencyTotal * (emergencyReductionRate / 100)
    const savingsAdmin = adminTimeCost * (adminReductionRate / 100)
    const savingsCompliance = complianceCost * (complianceReductionRate / 100)
    const savingsTotal = savingsEquipment + savingsEmergency + savingsAdmin + savingsCompliance

    // Step 4 — net savings, ROI %, and payback period.
    const netSavings = savingsTotal - planAnnualCost
    const monthlySavings = savingsTotal / 12
    const hasPlanCost = planAnnualCost > 0
    // ROI % is undefined with no plan cost to divide by (Free tier, or a
    // manually-entered $0). Payback is "never" when there's a plan cost but
    // no measurable savings to recoup it with.
    const roiPercent = hasPlanCost ? (netSavings / planAnnualCost) * 100 : null
    let paybackDays
    if (!hasPlanCost) {
      paybackDays = 0
    } else if (monthlySavings <= 0) {
      paybackDays = null
    } else {
      paybackDays = (planAnnualCost / monthlySavings) * 30
    }
    const paybackMonths = paybackDays === null ? null : paybackDays / 30

    return {
      equipmentValue, lostPercent, lostEquipmentCost,
      avgRepairCost, repairsPerYear, preventableRate, emergencyTotal, emergencyWaste,
      hoursPerWeek, hourlyRate, adminTimeCost,
      complianceFines, auditHours, auditHourlyRate, complianceCost,
      currentTotal,
      isFree, monthlyCost, planAnnualCost, hasPlanCost,
      equipmentRecoveryRate, emergencyReductionRate, adminReductionRate, complianceReductionRate,
      savingsEquipment, savingsEmergency, savingsAdmin, savingsCompliance, savingsTotal,
      netSavings, roiPercent, paybackMonths, paybackDays
    }
  }

  render(r) {
    const currency = (n) => `$${Math.round(n).toLocaleString("en-US")}`
    const percent = (n) => `${Math.round(n).toLocaleString("en-US")}%`

    this.outLostEquipmentTarget.textContent = currency(r.lostEquipmentCost)
    this.outEmergencyTotalTarget.textContent = currency(r.emergencyTotal)
    this.outEmergencyWasteTarget.textContent = currency(r.emergencyWaste)
    this.outAdminTimeTarget.textContent = currency(r.adminTimeCost)
    this.outComplianceTarget.textContent = currency(r.complianceCost)
    this.outCurrentTotalTarget.textContent = currency(r.currentTotal)

    this.outPlanAnnualTarget.textContent = currency(r.planAnnualCost)

    this.outSavingsEquipmentTarget.textContent = currency(r.savingsEquipment)
    this.outSavingsEmergencyTarget.textContent = currency(r.savingsEmergency)
    this.outSavingsAdminTarget.textContent = currency(r.savingsAdmin)
    this.outSavingsComplianceTarget.textContent = currency(r.savingsCompliance)
    this.outSavingsTotalTarget.textContent = currency(r.savingsTotal)

    this.outNetSavingsTarget.textContent = currency(r.netSavings)
    this.outRoiPercentTarget.textContent = r.roiPercent === null ? t("javascript.controllers.roi_calculator_controller.roi_no_plan_cost") : percent(r.roiPercent)
    if (!r.hasPlanCost) {
      this.outPaybackDaysTarget.textContent = "0"
      this.outPaybackMonthsTarget.textContent = t('javascript.controllers.roi_calculator_controller.immediate_0_plan_cost')
    } else if (r.paybackDays === null) {
      this.outPaybackDaysTarget.textContent = "\u2014"
      this.outPaybackMonthsTarget.textContent = t('javascript.controllers.roi_calculator_controller.never_no_measurable_savings')
    } else {
      this.outPaybackDaysTarget.textContent = Math.round(r.paybackDays).toLocaleString("en-US")
      this.outPaybackMonthsTarget.textContent = t("javascript.controllers.roi_calculator_controller.months_value", { months: r.paybackMonths.toFixed(2) })
    }
  }

  buildReportText(r) {
    const currency = (n) => `$${Math.round(n).toLocaleString("en-US")}`
    const esc = (s) => String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    const roi = r.roiPercent === null ? t("javascript.controllers.roi_calculator_controller.report.roi_na") : `${Math.round(r.roiPercent).toLocaleString("en-US")}%`
    const payback = !r.hasPlanCost
      ? t("javascript.controllers.roi_calculator_controller.immediate_0_plan_cost")
      : (r.paybackDays === null
        ? t("javascript.controllers.roi_calculator_controller.never_no_measurable_savings")
        : t("javascript.controllers.roi_calculator_controller.report.payback_days_months", { days: Math.round(r.paybackDays).toLocaleString("en-US"), months: r.paybackMonths.toFixed(2) }))
    const presetKey = this.hasIndustryPresetTarget ? this.industryPresetTarget.value : ""
    const preset = this.constructor.INDUSTRY_PRESETS[presetKey]
    const presetLabel = preset ? t(`javascript.controllers.roi_calculator_controller.${preset.labelKey}`) : t("javascript.controllers.roi_calculator_controller.report.custom_general")

    return [
      `<h3>${esc(t("javascript.controllers.roi_calculator_controller.report.title"))}</h3>`,
      `<p>${esc(t("javascript.controllers.roi_calculator_controller.report.scenario"))} <strong>${esc(presetLabel)}</strong></p>`,
      `<h4>${esc(t("javascript.controllers.roi_calculator_controller.report.step1"))}</h4>`,
      "<ul>",
      `<li>${esc(t("javascript.controllers.roi_calculator_controller.report.lost_equipment", { cost: currency(r.lostEquipmentCost), percent: r.lostPercent, value: currency(r.equipmentValue) }))}</li>`,
      `<li>${esc(t("javascript.controllers.roi_calculator_controller.report.emergency_waste", { cost: currency(r.emergencyWaste), percent: r.preventableRate, total: currency(r.emergencyTotal) }))}</li>`,
      `<li>${esc(t("javascript.controllers.roi_calculator_controller.report.manual_tracking", { cost: currency(r.adminTimeCost), hours: r.hoursPerWeek, rate: currency(r.hourlyRate) }))}</li>`,
      `<li>${esc(t("javascript.controllers.roi_calculator_controller.report.compliance_costs", { cost: currency(r.complianceCost) }))}</li>`,
      `<li><strong>${esc(t("javascript.controllers.roi_calculator_controller.report.total_current", { cost: currency(r.currentTotal) }))}</strong></li>`,
      "</ul>",
      `<h4>${esc(t("javascript.controllers.roi_calculator_controller.report.step2"))}</h4>`,
      `<ul><li><strong>${esc(t("javascript.controllers.roi_calculator_controller.report.per_year", { cost: currency(r.planAnnualCost) }))}</strong> (${esc(t("javascript.controllers.roi_calculator_controller.report.per_month", { cost: currency(r.monthlyCost) }))}${r.isFree ? esc(t("javascript.controllers.roi_calculator_controller.report.free_tier_suffix")) : ""})</li></ul>`,
      `<h4>${esc(t("javascript.controllers.roi_calculator_controller.report.step3"))}</h4>`,
      "<ul>",
      `<li>${esc(t("javascript.controllers.roi_calculator_controller.report.savings_equipment", { percent: r.equipmentRecoveryRate, cost: currency(r.savingsEquipment) }))}</li>`,
      `<li>${esc(t("javascript.controllers.roi_calculator_controller.report.savings_emergency", { percent: r.emergencyReductionRate, cost: currency(r.savingsEmergency) }))}</li>`,
      `<li>${esc(t("javascript.controllers.roi_calculator_controller.report.savings_admin", { percent: r.adminReductionRate, cost: currency(r.savingsAdmin) }))}</li>`,
      `<li>${esc(t("javascript.controllers.roi_calculator_controller.report.savings_compliance", { percent: r.complianceReductionRate, cost: currency(r.savingsCompliance) }))}</li>`,
      `<li><strong>${esc(t("javascript.controllers.roi_calculator_controller.report.total_savings", { cost: currency(r.savingsTotal) }))}</strong></li>`,
      "</ul>",
      `<h4>${esc(t("javascript.controllers.roi_calculator_controller.report.step4"))}</h4>`,
      "<ul>",
      `<li>${esc(t("javascript.controllers.roi_calculator_controller.report.net_savings", { cost: currency(r.netSavings) }))}</li>`,
      `<li>${esc(t("javascript.controllers.roi_calculator_controller.report.roi", { roi }))}</li>`,
      `<li>${esc(t("javascript.controllers.roi_calculator_controller.report.payback_period", { payback }))}</li>`,
      "</ul>",
      `<p><em>${esc(t("javascript.controllers.roi_calculator_controller.report.generated_by"))}</em></p>`
    ].join("\n")
  }
}
